import { useState, useEffect } from 'react';
import { ResumeContent } from '@/types/resume-builder';
import { supabase } from '@/integrations/supabase/client';

// Safe UUID generation that works in all iOS contexts including private browsing
const generateSafeId = (): string => {
  try {
    return crypto.randomUUID();
  } catch (e) {
    // Fallback for iOS private browsing or restricted contexts
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
};

interface GuestResumeData {
  id: string;
  content: ResumeContent;
  template_id?: string;
  created_at: string;
  updated_at: string;
}

interface GuestInterviewData {
  resumeText?: string;
  analysisData?: any;
  jobRoles?: any[];
  selectedRole?: string;
  gapAnalysis?: any;
  currentStep: number;
  sessionId: string;
}

const GUEST_RESUME_KEY = 'guest_resume_data';
const GUEST_INTERVIEW_KEY = 'guest_interview_data';

export function useGuestSession() {
  const [guestResumeData, setGuestResumeData] = useState<GuestResumeData | null>(null);
  const [guestInterviewData, setGuestInterviewData] = useState<GuestInterviewData | null>(null);

  // Load guest data on mount - wrapped in try-catch for iOS private browsing
  useEffect(() => {
    try {
      const savedResumeData = localStorage.getItem(GUEST_RESUME_KEY);
      const savedInterviewData = localStorage.getItem(GUEST_INTERVIEW_KEY);

      if (savedResumeData) {
        try {
          setGuestResumeData(JSON.parse(savedResumeData));
        } catch (error) {
          console.error('Error parsing guest resume data:', error);
          localStorage.removeItem(GUEST_RESUME_KEY);
        }
      }

      if (savedInterviewData) {
        try {
          setGuestInterviewData(JSON.parse(savedInterviewData));
        } catch (error) {
          console.error('Error parsing guest interview data:', error);
          localStorage.removeItem(GUEST_INTERVIEW_KEY);
        }
      }
    } catch (e) {
      console.warn('[useGuestSession] localStorage access denied:', e);
      // Continue without guest data - not a critical failure on iOS private browsing
    }
  }, []);

  // Clear guest data on user logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        console.log('User signed out - clearing guest data');
        clearGuestData();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const saveGuestResumeData = (data: Partial<GuestResumeData>) => {
    const current = guestResumeData || {
      id: generateSafeId(),
      content: {} as ResumeContent,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const updated = {
      ...current,
      ...data,
      updated_at: new Date().toISOString()
    };

    setGuestResumeData(updated);
    localStorage.setItem(GUEST_RESUME_KEY, JSON.stringify(updated));
  };

  const saveGuestInterviewData = (data: Partial<GuestInterviewData>) => {
    const current = guestInterviewData || {
      currentStep: 1,
      sessionId: generateSafeId()
    };

    const updated = {
      ...current,
      ...data
    };

    setGuestInterviewData(updated);
    localStorage.setItem(GUEST_INTERVIEW_KEY, JSON.stringify(updated));
  };

  const clearGuestData = () => {
    setGuestResumeData(null);
    setGuestInterviewData(null);
    localStorage.removeItem(GUEST_RESUME_KEY);
    localStorage.removeItem(GUEST_INTERVIEW_KEY);
  };

  const hasGuestData = () => {
    return !!(guestResumeData || guestInterviewData);
  };

  return {
    guestResumeData,
    guestInterviewData,
    saveGuestResumeData,
    saveGuestInterviewData,
    clearGuestData,
    hasGuestData
  };
}